import { useMemo, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { useTheme } from '../providers/ThemeProvider'
import {
  COLOR_TOKENS,
  GEOMETRY_TOKENS,
  contrastRatio,
  isSafeColor,
  luminance,
  rasterise,
  type ColorTokenId,
  type GeometryTokenId,
} from '../../theme/contract'

function readToken(id: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(id).trim()
}

function toHex(rgb: number[]): string {
  return '#' + rgb.map((n) => n.toString(16).padStart(2, '0')).join('')
}

export function ThemeStudio() {
  const { userOverride, setUserOverrideToken, resetUserOverride } = useTheme()
  const [tick, setTick] = useState(0)
  const refresh = () => setTick((n) => n + 1)

  const resolved = useMemo(() => {
    const out: Record<string, string> = {}
    for (const t of COLOR_TOKENS) out[t.id] = readToken(t.id)
    return out
    // tick and userOverride both force a re-read after an override lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, userOverride])

  const surface = resolved['--bg-surface'] || '#ffffff'
  const surfaceRgb = rasterise(surface)

  const warnings = COLOR_TOKENS.filter((t) => t.group === 'ink' || t.group === 'brand' || t.group === 'status')
    .map((t) => {
      const fg = rasterise(resolved[t.id] || '#000', surface)
      const ratio = contrastRatio(fg, surfaceRgb)
      const floor = t.id === '--text-primary' || t.id === '--text-secondary' ? 4.5 : 3
      return { id: t.id, label: t.label, ratio, floor, ok: ratio >= floor }
    })

  const elevationOk = (() => {
    const page = rasterise(resolved['--bg-default'] || '#eee')
    const card = rasterise(resolved['--bg-surface'] || '#fff')
    return luminance(card) > luminance(page)
  })()

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">Simple knobs</h2>
            <p className="text-sm text-secondary">Density only. These cannot make the app unusable — every length is clamped.</p>
          </div>
          {Object.keys(userOverride).length > 0 && (
            <button
              type="button"
              onClick={() => { resetUserOverride(); refresh() }}
              className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-primary"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset all overrides
            </button>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {GEOMETRY_TOKENS.map((t) => {
            const current = userOverride[t.id] ?? readToken(t.id) ?? `${(t.min + t.max) / 2}${t.unit}`
            const numeric = parseFloat(current) || t.min
            return (
              <label key={t.id} className="block">
                <span className="flex justify-between text-sm text-secondary mb-1">
                  <span>{t.label}</span>
                  <span className="text-muted">{numeric.toFixed(2)}{t.unit}</span>
                </span>
                <input
                  type="range"
                  min={t.min}
                  max={t.max}
                  step={t.step}
                  value={numeric}
                  onChange={(e) => {
                    setUserOverrideToken(t.id as GeometryTokenId, `${e.target.value}${t.unit}`)
                    refresh()
                  }}
                  className="w-full"
                />
              </label>
            )
          })}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-primary mb-1">Every token</h2>
        <p className="text-sm text-secondary mb-3">
          Change a colour and the rest of the app follows. Contrast is checked live against the card surface.
        </p>
        {!elevationOk && (
          <p className="mb-3 text-sm text-bad">
            The card is darker than the page — elevation is inverted. Lighten the card or darken the page.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {COLOR_TOKENS.map((t) => {
            const value = userOverride[t.id] ?? toPickerHex(resolved[t.id])
            const warn = warnings.find((w) => w.id === t.id)
            return (
              <label key={t.id} className="flex items-center gap-3 rounded-xl border border-soft bg-surface p-3">
                <input
                  type="color"
                  value={value.startsWith('#') && value.length === 7 ? value : '#888888'}
                  onChange={(e) => {
                    const next = e.target.value
                    if (isSafeColor(next)) {
                      setUserOverrideToken(t.id as ColorTokenId, next)
                      refresh()
                    }
                  }}
                  className="h-10 w-10 shrink-0 cursor-pointer rounded border border-soft bg-transparent"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-primary">{t.label}</span>
                  <span className="block text-app-micro text-muted truncate">{t.id}</span>
                  {warn && (
                    <span className={`block text-app-micro ${warn.ok ? 'text-ok' : 'text-bad'}`}>
                      {warn.ratio.toFixed(1)}:1 {warn.ok ? 'passes' : `needs ${warn.floor}:1`}
                    </span>
                  )}
                </span>
                {userOverride[t.id] && (
                  <button
                    type="button"
                    onClick={() => { setUserOverrideToken(t.id as ColorTokenId, null); refresh() }}
                    className="text-xs text-secondary hover:text-primary"
                  >
                    reset
                  </button>
                )}
              </label>
            )
          })}
        </div>
      </section>
    </div>
  )
}

function toPickerHex(raw: string): string {
  if (!raw) return '#888888'
  if (raw.startsWith('#') && (raw.length === 7 || raw.length === 4)) {
    if (raw.length === 4) {
      return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`
    }
    return raw
  }
  return toHex(rasterise(raw))
}
